import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  Globe2,
  Layers3,
  ShieldCheck,
  Smartphone,
  TrendingUp,
  Zap,
} from "lucide-react";

const featureCards = [
  {
    icon: BarChart3,
    title: "Institutional-grade analytics",
    description:
      "Track expectancy, drawdown, profit factor, and execution patterns in one command center.",
  },
  {
    icon: ShieldCheck,
    title: "Built for trust",
    description:
      "Keep your records organized with reliable access, clear history, and a workspace designed for disciplined review.",
  },
  {
    icon: Smartphone,
    title: "Mobile-first journaling",
    description:
      "Capture trades immediately on Android and iPhone without compromising speed or clarity.",
  },
  {
    icon: Layers3,
    title: "Trader and team workflows",
    description:
      "Support solo traders, mentors, funded accounts, and prop teams from the same product core.",
  },
];

const platformStats = [
  { label: "Multi-asset support", value: "Forex, crypto, stocks, indices" },
  { label: "Built for", value: "Retail traders, funded accounts, and trading teams" },
  { label: "Access", value: "Web platform with fast mobile-friendly journaling" },
];

const productPillars = [
  {
    title: "Execution Intelligence",
    body: "See which setups, sessions, and behaviors produce real edge instead of relying on memory.",
  },
  {
    title: "Capital Protection",
    body: "Surface drawdowns, loss streaks, and discipline breakdowns early before they damage the account.",
  },
  {
    title: "African Market Relevance",
    body: "Support local context with NGN-aware reporting, mobile access, and region-first product thinking.",
  },
];

function Home() {
  return (
    <div className="min-h-screen bg-[#f3efe7] text-slate-950">
      <section className="relative overflow-hidden border-b border-black/5 bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.2),_transparent_24%),radial-gradient(circle_at_80%_20%,_rgba(14,165,233,0.14),_transparent_18%),linear-gradient(180deg,_#f8f5ef_0%,_#efe7db_100%)]">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-400/50 to-transparent" />
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-[1.1fr_0.9fr] lg:px-10 lg:py-28">
          <div>
            <motion.p
              className="text-xs font-semibold uppercase tracking-[0.4em] text-slate-500"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              MekEdge Trading Journal
            </motion.p>
            <motion.h1
              className="mt-5 max-w-4xl font-serif text-5xl leading-none tracking-tight text-slate-950 sm:text-6xl lg:text-7xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
            >
              A serious trading journal for the next generation of African traders.
            </motion.h1>
            <motion.p
              className="mt-6 max-w-2xl text-lg leading-8 text-slate-700"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              Move beyond spreadsheets. Log trades, analyze performance, manage funded-account discipline,
              and build a track record that looks credible to mentors, teams, and serious capital.
            </motion.p>
            <motion.div
              className="mt-9 flex flex-col gap-4 sm:flex-row"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
            >
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-7 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                Start Free
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white/80 px-7 py-3 text-sm font-semibold text-slate-900 backdrop-blur transition hover:bg-white"
              >
                Explore Plans
              </Link>
            </motion.div>
            <motion.div
              className="mt-12 grid gap-4 sm:grid-cols-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.35 }}
            >
              {platformStats.map((item) => (
                <div key={item.label} className="rounded-3xl border border-slate-300/70 bg-white/70 p-4 shadow-[0_20px_60px_-40px_rgba(15,23,42,0.45)] backdrop-blur">
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-400">{item.label}</p>
                  <p className="mt-3 text-sm font-medium leading-6 text-slate-700">{item.value}</p>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.97, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <div className="absolute -left-8 top-16 hidden h-28 w-28 rounded-full bg-amber-300/40 blur-3xl lg:block" />
            <div className="absolute -right-6 bottom-12 hidden h-36 w-36 rounded-full bg-sky-300/30 blur-3xl lg:block" />
            <div className="relative overflow-hidden rounded-[32px] border border-slate-300/60 bg-[#0f172a] p-6 text-white shadow-[0_50px_120px_-50px_rgba(15,23,42,0.9)]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-sky-200">Trader cockpit</p>
                  <h2 className="mt-3 text-2xl font-semibold">Performance visibility at a glance</h2>
                </div>
                <TrendingUp className="h-8 w-8 text-emerald-300" />
              </div>
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="rounded-3xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                  <p className="text-xs uppercase tracking-[0.25em] text-slate-300">Net P&amp;L</p>
                  <p className="mt-3 text-3xl font-semibold text-emerald-300">+$12.4k</p>
                  <p className="mt-2 text-sm text-slate-300">≈ ₦18.6m across structured, tagged trades</p>
                </div>
                <div className="rounded-3xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                  <p className="text-xs uppercase tracking-[0.25em] text-slate-300">Profit factor</p>
                  <p className="mt-3 text-3xl font-semibold">2.31</p>
                  <p className="mt-2 text-sm text-slate-300">Edge that survives review</p>
                </div>
                <div className="col-span-2 rounded-[28px] border border-white/10 bg-gradient-to-r from-white/10 to-sky-400/10 p-5">
                  <div className="flex items-center justify-between text-sm text-slate-300">
                    <span>Execution discipline</span>
                    <span>Last 90 days</span>
                  </div>
                  <div className="mt-5 flex items-end gap-3">
                    {[38, 64, 52, 80, 73, 92, 88].map((height, index) => (
                      <div key={height} className="flex flex-1 flex-col items-center gap-2">
                        <div
                          className={`w-full rounded-full ${index === 5 ? "bg-emerald-300" : "bg-white/60"}`}
                          style={{ height }}
                        />
                        <span className="text-[10px] uppercase tracking-[0.25em] text-slate-400">
                          W{index + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex items-center justify-between rounded-3xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-slate-300">
                <span className="inline-flex items-center gap-2">
                  <Globe2 className="h-4 w-4 text-sky-300" />
                  Built for traders across Nigeria and the wider African trading community.
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-slate-400">Why traders choose MekEdge</p>
          <h2 className="mt-4 font-serif text-4xl tracking-tight text-slate-950">Everything you need to review trades with clarity, discipline, and confidence.</h2>
        </div>
        <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {featureCards.map(({ icon: Icon, title, description }) => (
            <motion.div
              key={title}
              className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_24px_60px_-45px_rgba(15,23,42,0.55)]"
              whileHover={{ y: -6 }}
              transition={{ duration: 0.2 }}
            >
              <div className="inline-flex rounded-2xl bg-[#f3efe7] p-3 text-slate-900">
                <Icon className="h-6 w-6" />
              </div>
              <h3 className="mt-6 text-xl font-semibold text-slate-950">{title}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-600">{description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-y border-black/5 bg-[#111827] text-white">
        <div className="mx-auto grid max-w-7xl gap-6 px-6 py-20 lg:grid-cols-3 lg:px-10">
          {productPillars.map((pillar) => (
            <div key={pillar.title} className="rounded-[28px] border border-white/10 bg-white/5 p-6 backdrop-blur">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sky-200">Pillar</p>
              <h3 className="mt-4 text-2xl font-semibold">{pillar.title}</h3>
              <p className="mt-4 text-sm leading-7 text-slate-300">{pillar.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
        <div className="rounded-[36px] bg-[linear-gradient(135deg,_#d97706,_#f59e0b_45%,_#fef3c7)] px-8 py-10 shadow-[0_30px_80px_-50px_rgba(180,83,9,0.8)] lg:px-12 lg:py-14">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-amber-900/60">Get started</p>
              <h2 className="mt-3 font-serif text-4xl tracking-tight text-amber-950">Start journaling trades with a workflow built for serious performance review.</h2>
              <p className="mt-4 text-base leading-8 text-amber-950/80">
                Record every trade, review what is working, spot costly habits early, and build a trading process you can trust over time.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center rounded-full bg-amber-950 px-7 py-3 text-sm font-semibold text-white transition hover:bg-black"
              >
                Create Account
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-full border border-amber-950/20 bg-white/80 px-7 py-3 text-sm font-semibold text-amber-950 transition hover:bg-white"
              >
                Talk to the Team
              </Link>
            </div>
          </div>
          <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-amber-950/10 bg-white/50 px-4 py-2 text-sm text-amber-950/80">
            <Zap className="h-4 w-4" />
            Trusted trade journaling, analytics, and accountability in one place.
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
